import React, { useId, useMemo, useState } from 'react'
import { DayPicker } from 'react-day-picker'
import { generateTimeOptions } from '../utils/formatters'
import 'react-day-picker/style.css'

export default function DateTimePicker({
  date, onDateChange,
  time, onTimeChange,
  partySize, onPartySizeChange,
  minGuests = 1,
  maxGuests,
  blockedDates = []
}) {
  const timeId = useId()
  const partySizeId = useId()
  const timeOptions = generateTimeOptions()

  // Draft state so typing is never clamped mid-keystroke ("25" was untypeable
  // when min is 10 — Math.max on every change turned "2" into 10). The parent
  // only ever sees a clamped numeric value, pushed on blur.
  const [partySizeDraft, setPartySizeDraft] = useState(null)

  const commitPartySize = (raw) => {
    const parsed = parseInt(raw, 10)
    const clamped = Number.isNaN(parsed)
      ? minGuests
      : Math.min(Math.max(parsed, minGuests), maxGuests || Infinity)
    onPartySizeChange(clamped)
    setPartySizeDraft(null)
  }

  // Minimum date is tomorrow
  const tomorrow = useMemo(() => {
    const d = new Date()
    d.setDate(d.getDate() + 1)
    d.setHours(0, 0, 0, 0)
    return d
  }, [])

  // Convert blocked date strings to Date objects for the disabled matcher
  const blockedDateObjects = useMemo(() => {
    return blockedDates.map(d => {
      const [year, month, day] = d.split('-').map(Number)
      return new Date(year, month - 1, day)
    })
  }, [blockedDates])

  // Convert current date string to Date object for selection
  const selectedDate = useMemo(() => {
    if (!date) return undefined
    const [year, month, day] = date.split('-').map(Number)
    return new Date(year, month - 1, day)
  }, [date])

  return (
    <div className="space-y-4">
      <div>
        <label className="block text-white text-sm font-medium mb-2">
          Date
        </label>
        <div className="bg-white rounded-lg p-3 flex justify-center">
          <DayPicker
            mode="single"
            selected={selectedDate}
            onSelect={(day) => {
              if (!day) return
              const yyyy = day.getFullYear()
              const mm = String(day.getMonth() + 1).padStart(2, '0')
              const dd = String(day.getDate()).padStart(2, '0')
              onDateChange(`${yyyy}-${mm}-${dd}`)
            }}
            disabled={[
              { before: tomorrow },
              ...blockedDateObjects
            ]}
            modifiers={{
              blocked: blockedDateObjects
            }}
            modifiersClassNames={{
              blocked: 'rdp-day--blocked'
            }}
            defaultMonth={selectedDate || tomorrow}
            required
          />
        </div>
        <style>{`
          .rdp-day--blocked:not(.rdp-selected) {
            color: #ef4444 !important;
            text-decoration: line-through;
          }
          .rdp-selected .rdp-day_button {
            background-color: #0ea5e9 !important;
            color: white !important;
          }
          .rdp-today:not(.rdp-selected) .rdp-day_button {
            font-weight: bold;
            color: #0ea5e9;
          }
        `}</style>
      </div>

      <div>
        <label htmlFor={timeId} className="block text-white text-sm font-medium mb-2">
          Time
        </label>
        <select
          id={timeId}
          value={time}
          onChange={(e) => onTimeChange(e.target.value)}
          required
          className="w-full px-4 py-3 rounded-lg border-0 text-gray-900 focus:outline-none focus:ring-2 focus:ring-sky-500 focus:ring-offset-2"
        >
          <option value="">Select a time</option>
          {timeOptions.map(t => (
            <option key={t} value={t}>{t}</option>
          ))}
        </select>
      </div>

      <div>
        <label htmlFor={partySizeId} className="block text-white text-sm font-medium mb-2">
          Number of Guests
        </label>
        <input
          id={partySizeId}
          type="number"
          inputMode="numeric"
          value={partySizeDraft ?? partySize}
          onChange={(e) => setPartySizeDraft(e.target.value)}
          onBlur={(e) => commitPartySize(e.target.value)}
          min={minGuests}
          max={maxGuests || undefined}
          required
          className="w-full px-4 py-3 rounded-lg border-0 text-gray-900 focus:outline-none focus:ring-2 focus:ring-sky-500 focus:ring-offset-2"
        />
        {(minGuests > 1 || maxGuests) && (
          <p className="text-sky-200 text-xs mt-1">
            This package takes {minGuests}{maxGuests ? `–${maxGuests}` : ' or more'} guests
          </p>
        )}
      </div>
    </div>
  )
}
